import { type AxiosResponse } from 'axios'
import { defaultTo, flow, get } from 'lodash/fp'
import { SerializableValue, Maybe } from '@axilon-apps/libs-shared-types'

const getWithDefault = <T extends SerializableValue>(
  path: string[],
  defaultValue: SerializableValue = null
): ((object: AxiosResponse | undefined) => T) => flow(get(path), defaultTo(defaultValue))

// fortress (v1) returned a single `message` string per error; axilon-api (v2)
// returns `messages: string[]`. Prefer the legacy field when present so v1
// callers are unaffected, fall back to the first v2 message otherwise.
const getMessage = (response: AxiosResponse | undefined): Maybe<string> => {
  const legacy = get(['data', 'message'], response) as Maybe<string>
  if (legacy) {
    return legacy
  }
  const v2 = get(['data', 'messages'], response) as Maybe<string[]>
  return v2?.[0] ?? null
}

// fortress (v1) returned a machine-readable `errorReason: string` (e.g.
// `machine_not_on_source_branch`). axilon-api (v2) doesn't have a direct
// equivalent — `code` is a numeric HTTP-like status (409, 422, …), NOT the
// reason key. Per stdlib `web.NewError(reason, code, httpStatus)`, the
// reason key is put into `messages[0]` and into `errors["error"][0]`.
// So we read `messages[0]` for the v2 reason. Caveat: for generic
// constructors (NewForbidden / NewConflict / …) `messages[0]` is the
// human-readable English message rather than a stable key — that's a real
// v1→v2 semantic gap, callers comparing against specific keys should be
// aware (currently only `use-resolve-conflicts.ts` does that, against
// endpoints that all use `web.NewError("specific_key", …)`).
const getReason = (response: AxiosResponse | undefined): Maybe<string> => {
  const legacy = get(['data', 'errorReason'], response) as Maybe<string>
  if (legacy) return legacy
  const v2 = get(['data', 'messages'], response) as Maybe<string[]>
  return v2?.[0] ?? null
}

const getters = {
  message: getMessage,
  reason: getReason,
  status: getWithDefault<number | string>(['status']),
  fields: getWithDefault<Record<string, string[]>>(['data', 'errors'], {})
}

export default getters
